/**
 * Which workspace the page is looking at.
 *
 * Every screen before this one was ORG-scoped — members, workspaces, audit,
 * profile — so each read `me.orgs[0]` for itself and nothing needed to be
 * shared. Story 6.1 is the first workspace-scoped page, and the workspace is
 * not `me.workspaces[0]`: it is the one chosen in the top bar, remembered in
 * `localStorage` and validated against `/v1/me` on every boot. `AppShell` owns
 * that resolution. A page that re-derived it would read the first workspace
 * while the switcher above it said a different name.
 *
 * It carries `me` as well, so a page inside the shell does not fire a second
 * `/v1/me` to learn the plan it is already rendering the usage pill from.
 *
 * A CONVENIENCE, NEVER AN AUTHORITY — the same thing `AppShell` says about the
 * remembered id. The server resolves `{wid}` from the path on every request and
 * answers 404 if the grant is not there, so nothing here is a permission.
 *
 * Story 6.1.
 */
import { createContext, useContext } from 'react'
import type { Me, MeOrg, MeWorkspace } from './useMe'

export type Shell = {
  me: Me
  /** Undefined only for an account with no workspace at all. */
  workspace: MeWorkspace | undefined
  org: MeOrg | undefined
}

export const ShellContext = createContext<Shell | null>(null)

/**
 * Throws outside the provider rather than returning null.
 *
 * A page that reaches this without a shell around it is a routing mistake, and
 * a null that every caller has to narrow would spread that mistake into every
 * page instead of failing where it happened.
 */
export function useShell(): Shell {
  const shell = useContext(ShellContext)
  if (!shell) {
    throw new Error(
      'useShell() outside <AppShell>. A workspace-scoped page must be routed with shell: true.',
    )
  }
  return shell
}
